import React from "react";
import { Card, CardBody, Col, Row, Table } from "reactstrap";
import {
  Timeline,
  type TimelineStep,
  type TimelineStepState,
} from "components/common/Timeline";
import { Link } from "react-router-dom";
import { DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import {
  EnquiryIntent,
  EnquiryIntentLabelMap,
  EnquiryStatus,
  enquiryStatusMap,
} from "features/Enquiries/constants/enquiry.constants";
import {
  formatSourceChannelLabel,
  priorityMap,
} from "features/Meta/constants/meta.constants";
import { useEnquiryOverview } from "./EnquiryOverviewContext";
import { getClientDisplayName } from "features/Clients/utils/clientDisplayName";
import type { Enquiry } from "features/Enquiries/types/enquiry.types";
import AssignmentsOverviewCard from "features/WorkAssignments/components/AssignmentsOverviewCard";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

function isTerminalEnquiryStatus(status: string | null | undefined): boolean {
  const s = String(status ?? "")
    .trim()
    .toUpperCase();
  return s === EnquiryStatus.CONVERTED || s === EnquiryStatus.CANCELLED;
}

const OverviewTab = () => {
  const { enquiry, refetch } = useEnquiryOverview();
  const { can } = useAccess();
  const canCreateAssignment = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canUpdateAssignment = can(Permissions.WORK_ASSIGNMENT.UPDATE);

  return (
    <Row>
      <Col lg={8}>
        <Card>
          <CardBody>
            <div className="text-muted">
              <h6 className="mb-3 text-uppercase">Enquiry info</h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    {enquiry?.code && (
                      <tr>
                        <th
                          className="ps-0"
                          scope="row"
                          style={{ width: "180px" }}
                        >
                          Code :
                        </th>
                        <td className="text-muted">{enquiry.code}</td>
                      </tr>
                    )}
                    <tr>
                      <th className="ps-0" scope="row">
                        Title :
                      </th>
                      <td className="text-muted">{enquiry?.title ?? "—"}</td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Source channel :
                      </th>
                      <td className="text-muted">
                        {formatSourceChannelLabel(enquiry?.sourceChannel)}
                      </td>
                    </tr>

                    <tr>
                      <th className="ps-0" scope="row">
                        Priority :
                      </th>
                      <td>
                        <StatusBadge
                          value={enquiry?.priorityId}
                          map={priorityMap}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Enquiry intent :
                      </th>
                      <td className="text-muted">
                        {enquiry?.enquiryIntent != null
                          ? (EnquiryIntentLabelMap[
                              enquiry.enquiryIntent as EnquiryIntent
                            ] ?? enquiry.enquiryIntent)
                          : "—"}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              {enquiry?.description ? (
                <div className="pt-3  mt-4">
                  <h6 className="mb-3 text-uppercase">Description</h6>
                  <p className="text-muted mb-0">{enquiry.description}</p>
                </div>
              ) : null}

              <div className="pt-3 border-top border-top-dashed mt-4">
                <Row className="gy-3">
                  {enquiry?.created_at && (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Created</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={enquiry.created_at} />
                        </h5>
                      </div>
                    </Col>
                  )}

                  {enquiry?.updated_at && (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Last updated
                        </p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={enquiry.updated_at} />
                        </h5>
                      </div>
                    </Col>
                  )}

                  <Col lg={3} sm={6}>
                    <div>
                      <p className="mb-2 text-uppercase fw-medium">Status :</p>
                      <h5 className="fs-14 mb-0">
                        <StatusBadge
                          value={enquiry?.status}
                          map={enquiryStatusMap}
                        />
                      </h5>
                    </div>
                  </Col>

                  {enquiry?.deleted_at != null && enquiry.deleted_at !== "" && (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium text-danger">
                          Deleted
                        </p>
                        <h5 className="fs-14 mb-0 text-danger">
                          <DateTime date={enquiry.deleted_at} />
                        </h5>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
      <Col lg={4}>
        {enquiry ? (
          <Card className="mb-3">
            <CardBody>
              <h6 className="text-uppercase mb-3 text-muted">Timeline</h6>
              <Timeline steps={buildEnquiryTimeline(enquiry)} />
            </CardBody>
          </Card>
        ) : null}
        {enquiry?.id ? (
          <AssignmentsOverviewCard
            sourceType={WorkAssignmentSourceType.ENQUIRY}
            sourceId={enquiry.id}
            sourceLabel={
              enquiry.code
                ? `Enquiry ${enquiry.code}`
                : enquiry.title
                  ? `Enquiry: ${enquiry.title}`
                  : `Enquiry #${enquiry.id}`
            }
            canAssign={canCreateAssignment && !isTerminalEnquiryStatus(enquiry.status)}
            canReassign={canUpdateAssignment && !isTerminalEnquiryStatus(enquiry.status)}
            onSuccess={() => void refetch()}
          />
        ) : null}
        {enquiry?.clientName ||
        enquiry?.clientEmail ||
        enquiry?.clientPhone ||
        enquiry?.clientId ? (
          <Card className="mb-3">
            <CardBody>
              <h6 className="text-uppercase text-muted mb-3">Client</h6>
              <div className="vstack gap-2">
                {enquiry.clientId != null && enquiry.client ? (
                  <Link
                    to={`/clients/${enquiry.clientId}`}
                    className="fw-semibold link"
                  >
                    {getClientDisplayName(enquiry.client) !== "—"
                      ? getClientDisplayName(enquiry.client)
                      : enquiry.clientName || `Client #${enquiry.clientId}`}
                  </Link>
                ) : enquiry.clientName ? (
                  <span className="fw-semibold">{enquiry.clientName}</span>
                ) : null}

                {enquiry.clientEmail ? (
                  <div className="d-flex align-items-center gap-2 text-muted small">
                    <i className="ri-mail-line flex-shrink-0" />
                    <a
                      href={`mailto:${enquiry.clientEmail}`}
                      className="text-muted"
                    >
                      {enquiry.clientEmail}
                    </a>
                  </div>
                ) : null}

                {enquiry.clientPhone ? (
                  <div className="d-flex align-items-center gap-2 text-muted small">
                    <i className="ri-phone-line flex-shrink-0" />
                    <a
                      href={`tel:${enquiry.clientPhone}`}
                      className="text-muted"
                    >
                      {enquiry.clientPhone}
                    </a>
                  </div>
                ) : null}

                {enquiry.clientAddress ? (
                  <div className="d-flex align-items-center gap-2 text-muted small">
                    <i className="ri-map-pin-line flex-shrink-0" />
                    <span>{enquiry.clientAddress}</span>
                  </div>
                ) : null}
              </div>
            </CardBody>
          </Card>
        ) : null}
      </Col>
    </Row>
  );
};

export default OverviewTab;

function buildEnquiryTimeline(enquiry: Enquiry): TimelineStep[] {
  const history = enquiry.statusHistory ?? [];
  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const label =
      enquiryStatusMap[entry.status as EnquiryStatus]?.label ?? entry.status;
    const state: TimelineStepState = isLast
      ? entry.status === EnquiryStatus.CANCELLED
        ? "skipped"
        : entry.status === EnquiryStatus.CONVERTED
          ? "done"
          : "active"
      : "done";
    return {
      label,
      timestamp: entry.at,
      by: entry.user
        ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
        : entry.by != null ? `#${entry.by}` : undefined,
      actor: entry.message ?? undefined,
      state,
    };
  });
}
